import { logEvent } from "@playbrain/config"
import { allowedCorsOrigin, enforceRateLimit } from "@server/contact-proxy"

/**
 * The PLAYDATA newsletter's public endpoints.
 *
 * Joining and leaving a mailing list, and nothing else. These do not go
 * through the contact mailer: a subscription is a database write, not a
 * message to an inbox, which is exactly what the microsite's signup form has
 * been pretending since it shipped.
 *
 * CORS and rate limiting are reused from `contact-proxy` rather than
 * reimplemented. The rate-limit key is namespaced, so filling in the contact
 * form does not spend the budget for subscribing — one shared map, two
 * separate buckets.
 */

type HandlerArgs = { request: Request }

type NewsletterDependencies = {
  siteOrigin?: string
  domainOrigin?: string
  subscribe?: SubscribeFn
  unsubscribe?: UnsubscribeFn
}

type SubscribeFn = (input: {
  email: string
  locale: string
  source: "site"
  signupIp: string | null
  signupUserAgent: string | null
}) => Promise<string>

type UnsubscribeFn = (
  subscriberId: string,
  token: string
) => Promise<{ outcome: string; email?: string; locale?: string }>

function corsHeaders(origin: string | null, dependencies: NewsletterDependencies) {
  const headers = new Headers({
    "Access-Control-Allow-Methods": "POST, OPTIONS",
    "Access-Control-Allow-Headers": "Content-Type",
  })
  const allowed = allowedCorsOrigin(origin, dependencies)
  if (allowed) headers.set("Access-Control-Allow-Origin", allowed)
  return headers
}

function clientIpOf(request: Request): string {
  return request.headers.get("x-forwarded-for")?.split(",")[0]?.trim() || "unknown"
}

/**
 * The client IP as something safe to store.
 *
 * `signup_ip` is `varchar(45)`, and the value comes from a header the client
 * controls — CloudFront APPENDS to an inbound `X-Forwarded-For` rather than
 * replacing it, so hop zero is whatever the caller sent. An over-long value
 * failed the INSERT with a 1406, rolling back the transaction and answering
 * 500, which made subscribing breakable with one curl. It is also not an
 * identity: it is kept because single opt-in leaves nothing else as a consent
 * record, and a forged one is still better than none.
 *
 * `dashboardOtpApi.server.ts` truncates the same header for the same reason.
 */
function consentIpOf(request: Request): string | null {
  const ip = clientIpOf(request)
  if (ip === "unknown") return null
  return ip.slice(0, 45)
}

function jsonWithCors(
  body: Record<string, unknown>,
  status: number,
  origin: string | null,
  dependencies: NewsletterDependencies
) {
  return new Response(JSON.stringify(body), {
    status,
    headers: new Headers({
      "Content-Type": "application/json",
      ...Object.fromEntries(corsHeaders(origin, dependencies)),
    }),
  })
}

async function parseJson(request: Request): Promise<Record<string, unknown> | null> {
  try {
    const parsed: unknown = await request.json()
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : null
  } catch {
    return null
  }
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : ""
}

function preflight(dependencies: NewsletterDependencies) {
  return async ({ request }: HandlerArgs) => {
    const origin = request.headers.get("origin")
    if (origin && !allowedCorsOrigin(origin, dependencies)) return new Response(null, { status: 403 })
    return new Response(null, { status: 204, headers: corsHeaders(origin, dependencies) })
  }
}

/**
 * Guards every public write here: origin allowlist, then rate limit.
 *
 * Returns a Response to send, or null to carry on.
 */
function guard(request: Request, bucket: string, dependencies: NewsletterDependencies) {
  const origin = request.headers.get("origin")
  if (origin && !allowedCorsOrigin(origin, dependencies)) {
    return jsonWithCors({ ok: false, message: "Origin not allowed" }, 403, origin, dependencies)
  }
  if (!enforceRateLimit(`${bucket}:${clientIpOf(request)}`)) {
    return jsonWithCors({ ok: false, message: "Too many requests" }, 429, origin, dependencies)
  }
  return null
}

export function createNewsletterSubscribeHandlers(dependencies: NewsletterDependencies = {}) {
  return {
    POST: async ({ request }: HandlerArgs) => {
      const origin = request.headers.get("origin")
      const blocked = guard(request, "newsletter-subscribe", dependencies)
      if (blocked) return blocked

      const body = await parseJson(request)
      if (!body) return jsonWithCors({ ok: false, message: "Invalid payload" }, 400, origin, dependencies)

      /*
       * The honeypot. A hidden field no person sees and every naive bot fills
       * in. Answering 200 rather than 400 is the point: a bot told it failed
       * tries again differently, while one told it succeeded goes away. The
       * contact form does the same thing.
       */
      if (asString(body.nickname).trim() !== "") {
        logEvent("info", "playdata_newsletter.honeypot", { ip: clientIpOf(request) })
        return jsonWithCors({ ok: true }, 200, origin, dependencies)
      }

      const email = asString(body.email).trim()
      if (email === "") {
        return jsonWithCors({ ok: false, message: "Email is required" }, 400, origin, dependencies)
      }

      try {
        const subscribe =
          dependencies.subscribe ??
          (async (input) => {
            const { subscribeToPlaydataNewsletter } = await import(
              "@api/features/playdata-newsletter/subscribersSql"
            )
            return subscribeToPlaydataNewsletter(input)
          })

        const outcome = await subscribe({
          email,
          locale: asString(body.locale) || "ja",
          source: "site",
          signupIp: consentIpOf(request),
          signupUserAgent: request.headers.get("user-agent")?.slice(0, 512) ?? null,
        })

        if (outcome === "invalid") {
          return jsonWithCors({ ok: false, message: "Invalid email" }, 400, origin, dependencies)
        }

        logEvent("info", "playdata_newsletter.subscribed", { outcome })

        /*
         * Every non-invalid outcome answers identically. Telling a visitor
         * "you are already subscribed" turns this form into an oracle for
         * whether an address is on the list, and telling someone their address
         * is suppressed explains how to evade the suppression.
         */
        return jsonWithCors({ ok: true }, 200, origin, dependencies)
      } catch (error) {
        logEvent("error", "playdata_newsletter.subscribe_failed", {
          message: error instanceof Error ? error.message : "Unknown error",
        })
        return jsonWithCors({ ok: false, message: "Could not subscribe" }, 500, origin, dependencies)
      }
    },
    OPTIONS: preflight(dependencies),
  }
}

export function createNewsletterUnsubscribeHandlers(dependencies: NewsletterDependencies = {}) {
  return {
    POST: async ({ request }: HandlerArgs) => {
      const origin = request.headers.get("origin")
      const blocked = guard(request, "newsletter-unsubscribe", dependencies)
      if (blocked) return blocked

      const body = await parseJson(request)
      if (!body) return jsonWithCors({ ok: false, message: "Invalid payload" }, 400, origin, dependencies)

      const subscriberId = asString(body.id)
      const token = asString(body.token)

      try {
        const { isSubscriberIdShaped, isUnsubscribeTokenShaped, hasNewsletterSecret } = await import(
          "@api/features/playdata-newsletter/unsubscribeToken"
        )

        /*
         * Without the secret no token can be verified, so every request would
         * look invalid. Saying so is the honest answer: an unsubscribe that
         * quietly fails is worse than one that admits it is broken, because
         * the person walks away believing they have left.
         */
        if (!hasNewsletterSecret()) {
          logEvent("error", "playdata_newsletter.missing_secret", {})
          return jsonWithCors({ ok: false, outcome: "unavailable" }, 503, origin, dependencies)
        }

        if (!isSubscriberIdShaped(subscriberId) || !isUnsubscribeTokenShaped(token)) {
          return jsonWithCors({ ok: false, outcome: "invalidLink" }, 400, origin, dependencies)
        }

        const unsubscribe =
          dependencies.unsubscribe ??
          (async (id: string, value: string) => {
            const { unsubscribeByLink } = await import("@api/features/playdata-newsletter/subscribersSql")
            return unsubscribeByLink(id, value)
          })

        const result = await unsubscribe(subscriberId, token)
        if (result.outcome === "invalidLink") {
          return jsonWithCors({ ok: false, outcome: "invalidLink" }, 400, origin, dependencies)
        }

        logEvent("info", "playdata_newsletter.unsubscribed", { outcome: result.outcome })
        return jsonWithCors({ ok: true, outcome: result.outcome }, 200, origin, dependencies)
      } catch (error) {
        logEvent("error", "playdata_newsletter.unsubscribe_failed", {
          message: error instanceof Error ? error.message : "Unknown error",
        })
        return jsonWithCors({ ok: false, outcome: "error" }, 500, origin, dependencies)
      }
    },
    OPTIONS: preflight(dependencies),
  }
}
